import { useMemo, useState } from "react";
import type { ReactNode } from "react";
import { useQuery } from "@apollo/client/react";
import { FormattedMessage, useIntl } from "react-intl";
import { BoxHeader } from "@/components/design/BoxHeader";
import { Button } from "@/components/design/Button";
import { FloatingActionRow } from "./FloatingActionRow";
import { ReadinessList } from "./ReadinessList";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { formatDate } from "@/lib/format-date";
import { useAssistantContext } from "@/lib/use-assistant-context";
import {
  BLANK_COMPANY_EXPERTISE_REQUIREMENT,
  COMPANY_AI_CEILING_EXCEEDED,
} from "@/lib/genesis-error-codes";
import { graphQlErrorExtensions } from "@/lib/graphql-error";
import { userErrorMessage } from "@/lib/user-error";
import { MatchResultCompaniesDocument } from "@/graphql/generated/operations";
import { CompanyStatus } from "@/graphql/generated/types";
import { SaveMatchModal } from "./MatchModals";
import { MatchResultPdfExportModal, type MatchResultPdfSourceResult } from "./MatchResultPdfExportModal";
import { useCompanyLogos } from "@/lib/use-company-logos";
import { useOpenedMatchResult } from "./use-opened-match-result";
import { CompanyRankingPanel } from "./CompanyRankingPanel";
import { OrganizationalRecordBadge } from "./OrganizationalRecordBadge";
import { useResultSections } from "./use-result-sections";
import { ResultSectionPicker } from "./ResultSectionPicker";
import {
  COMPANY_RESULT_SECTION_IDS,
  companyResultSectionLabels,
} from "./result-sections";
import {
  buildCompanyRanking,
  type CompanyRankingRowInput,
  type RankedCompany,
} from "./company-ranking-derive";
import { useMatchCompanyPool } from "./use-match-company-pool";
import { incompleteCompanyConditionCount } from "./match-design-rules";
import { resolveMatchMode } from "./match-mode";
import type { MatchData } from "./match-design-types";
import type { MatchResultListItem, UseMatchEditorResult } from "./use-match-editor";

// STEP 3 OF A COMPANY MATCH: the ranking.
//
// A SCREEN, not a panel inside the person result step. That step is built around a team the solver
// composed — a pre-solve seat arithmetic, a team dashboard, power boxes, a saved-results list that
// counts candidates — and a company run produces none of it: genesis branches to the company arm
// BEFORE the solver, so there is no team, no team profile, no power model and no seat to reason
// about. Sharing the screen would have meant a person surface with most of itself switched off.
//
// WHERE THE RANKING COMES FROM. `generateTransientMatchResult` is the same mutation the person path
// calls; genesis reads the immutable matchMode and takes the company arm, which ranks every company
// in the pool on its own and persists the rows plus a draft result. The rows are then read by id
// (`MatchResultCompanies`) rather than out of the mutation payload, because genesis answers
// `MatchResult.companies` from the database for both — one source for the fresh and the saved
// ranking, so they cannot disagree.

export function CompanyRankingStep({
  data,
  editor,
  backSlot,
  solveProgress,
}: {
  data: MatchData;
  editor: UseMatchEditorResult;
  solveProgress: ReactNode;
  // The workflow's back button. This surface has no save row of its own, so the
  // row below holds it alone — a company ranking still needs the way back that
  // bd-requirements#27 was about.
  backSlot?: ReactNode;
}) {
  const intl = useIntl();
  // Welches gespeicherte Ergebnis offen ist, samt ?result=-Spiegelung und Cold-Restore nach
  // Browser-Zurück. Bis hierher war das ein reines useState, weshalb der Weg "Ergebnis öffnen,
  // Unternehmen anklicken, Browser-Zurück" auf der Ergebnisliste von Schritt 3 endete statt im
  // Ergebnis. Der Personen-Step trägt dieselbe Anbindung.
  const {
    openedResult,
    openError,
    open: openSavedResult,
    close: closeSavedResult,
  } = useOpenedMatchResult(editor.openResult);
  const [saveOpen, setSaveOpen] = useState(false);
  const { hiddenSections, saving: sectionsSaving, error: sectionsError, change: handleSectionsChange } =
    useResultSections(openedResult, editor.updateResultSections);
  const [sectionsOpen, setSectionsOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);

  // The pool is read here too — not for its rows but for the SHORTLIST HANDLE. The tick is drawn on
  // a ranking row and written on the matching pool row, so the two have to be joined by company id,
  // and the pool is the side that owns the live mark. Same query and variables as the pool step, so
  // Apollo answers this from cache when the user walks back and forth between the steps.
  const pool = useMatchCompanyPool({ matchId: editor.matchId, canEdit: editor.canEditPool });

  // A saved ranking wins over the live one: opening one is an explicit act, and the live draft is
  // still there behind it (closing brings it back).
  const liveResultId = editor.solverResult?.id ?? null;
  const shownResultId = openedResult?.id ?? liveResultId;
  const isSaved = openedResult !== null;

  const rankingQuery = useQuery(MatchResultCompaniesDocument, {
    variables: { id: shownResultId ?? "" },
    skip: shownResultId == null,
    fetchPolicy: "cache-and-network",
  });

  const rankingRows = useMemo<CompanyRankingRowInput[]>(
    () =>
      (rankingQuery.data?.matchResult?.companies ?? []).map((entry) => ({
        entryId: entry.entryId,
        position: entry.position,
        // On a saved ranking this is HISTORY — the mark as it stood when the run was saved (concept
        // v14, decision 9). It is never written back; the live mark lives on the pool row.
        result: entry.result ?? null,
        company: entry.company
          ? {
              id: entry.company.id,
              name: entry.company.name,
              inactive: entry.company.status === CompanyStatus.Inactive,
              industryId: entry.company.industry?.id ?? null,
              employeeCount: entry.company.employeeCount ?? null,
            }
          : null,
      })),
    [rankingQuery.data],
  );

  const ranked = useMemo(() => buildCompanyRanking(rankingRows), [rankingRows]);

  /**
   * LOGOS FOR THE RANKING ROWS, hydrated here because this is the Apollo-aware half.
   *
   * `Company.logo` travels base64 inside the response, so genesis resolves it on four surfaces only
   * and offers `companyIdentities` — ids in, name and logo out, server-capped — for exactly a list
   * like this one. `useCompanyLogos` drives that in rounds of 50 and asks for nothing it already
   * holds. Asked once for the whole ranking rather than per row, so the rounds are shared; the
   * panel takes the lookup as a prop and stays a presentational component.
   */
  const { logoOf } = useCompanyLogos(
    useMemo(() => ranked.map((row) => row.companyId), [ranked]),
  );
  const hasRanking = shownResultId != null && ranked.length > 0;
  const exportResult = useMemo<MatchResultPdfSourceResult | null>(() => {
    if (openedResult) return openedResult;
    if (!hasRanking || !editor.solverResult) return null;
    return {
      ...editor.solverResult,
      companies: rankingQuery.data?.matchResult?.companies ?? [],
    };
  }, [editor.solverResult, hasRanking, openedResult, rankingQuery.data]);
  // A ranking whose AI judgement saw a company's organisational record is answered only to record
  // readers (GENESIS-0060 §5), so the runner gets the same marker as on a person result: it explains
  // why colleagues without the right see fewer rankings.
  const usesOrganizationalRecord = openedResult
    ? openedResult.usesOrganizationalRecord === true
    : hasRanking && editor.solverResult?.usesOrganizationalRecord === true;

  // genesis answered, and the result this screen is showing is not there: deleted under the reader,
  // or never visible to them. No GraphQL error comes with it, so nothing but this flag separates it
  // from a ranking that legitimately holds no companies.
  const missingResult = rankingQuery.data != null && rankingQuery.data.matchResult === null;

  // THE READ'S OWN FAILURE. Three different absences used to arrive at the panel as the same empty
  // array and be told as one sentence, "the pool of this match is empty": a failed read, a result
  // that is gone, and a pool nobody filled. Only the last is that sentence, and on this branch it
  // is not even reachable — the run is gated on a non-empty pool, so a match that produced a result
  // cannot have an empty one. Computed once per error, because userErrorMessage logs a diagnostic
  // per call (@adr SPARK-0010-no-raw-server-errors-in-ui@1) and a message rebuilt on every render would log on every render.
  const rankingReadError = useMemo(() => {
    if (shownResultId == null) return null;
    if (rankingQuery.error) return userErrorMessage(intl, rankingQuery.error);
    // The same generic sentence and the same diagnostic as a transport failure, deliberately: the
    // reader's situation is identical (the ranking cannot be shown) and spark knows no more than
    // that, so a second, invented explanation would claim knowledge it does not have. The Error is
    // never rendered (@adr SPARK-0010-no-raw-server-errors-in-ui@1) — it is what lands in the console for whoever debugs it.
    if (missingResult) return userErrorMessage(intl, new Error(`MatchResult ${shownResultId} not found`));
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rankingQuery.error, missingResult, shownResultId]);

  // THE POOL'S SIZE IS ONLY A FACT ONCE THE READ HAS ANSWERED, and `pool.total` cannot say so on
  // its own: it falls back to 0 while the read is in flight and after it failed, which is the very
  // number an empty pool reports. null is this screen's "not known yet", so every consumer below
  // has to decide what to do with it instead of printing a zero it was handed.
  const poolTotal = pool.loading || pool.readError !== null ? null : pool.total;

  // A run is refused while a criterion is still half-written, and the reason is printed rather than
  // left to genesis: an unpicked Branche reaches it as an empty UUID, and a blank Fachwissen wording
  // reaches the MODEL as the question "can this company do ''". Company criteria only — the person
  // arm of the design is never evaluated in this mode, so it must not block this button.
  const incompleteCriteria = incompleteCompanyConditionCount(data);

  const criteriaCount =
    (data.companyConditions ?? []).length + (data.unknownCompanyConditions ?? []).length;


  // View state only (@external-adr AGENT-SERVICE-0020-ag-ui-protocol-adoption): sizes and which view is open, never the ranked companies.
  useAssistantContext([
    {
      description: "the company ranking view currently open",
      value: [
        `match mode ${resolveMatchMode(data.matchMode)}`,
        isSaved
          ? `saved ranking "${openedResult?.name || "unnamed"}"`
          : hasRanking
            ? "newly calculated, not-yet-saved ranking"
            : "no ranking calculated yet",
        // Same zero, same trap: an unread pool would otherwise be reported to the assistant as an
        // empty one, and the assistant would repeat the false diagnosis back to the user.
        poolTotal == null ? "pool size not loaded" : `${poolTotal} companies in the pool`,
        `${criteriaCount} company criteria`,
      ]
        .join("; ")
        .slice(0, 1_900),
    },
  ]);

  // Computed once per error rather than per render: userErrorMessage logs a diagnostic as a side
  // effect (@adr SPARK-0010-no-raw-server-errors-in-ui@1), and a message rebuilt on every render would log on every render.
  // The two refusals genesis catalogues for a company RUN are named here; anything else falls to the
  // generic message plus a support code (@adr SPARK-0010-no-raw-server-errors-in-ui@1). Naming them is not decoration: both are repairable
  // by the reader, and a generic sentence offers a retry that cannot succeed — the ceiling needs fewer
  // expertise criteria (or a higher configured one) and the blank row needs filling in.
  const runError = useMemo(
    () => {
      if (!editor.solverError || editor.solverLoading) return null;
      if (graphQlErrorExtensions(editor.solverError, COMPANY_AI_CEILING_EXCEEDED)) {
        return intl.formatMessage({
          id: "match.companyRanking.aiCeilingExceeded",
          defaultMessage:
            "This ranking needs more AI judgements than one run may make. Reduce the expertise criteria, or ask an administrator to raise the limit.",
        });
      }
      if (graphQlErrorExtensions(editor.solverError, BLANK_COMPANY_EXPERTISE_REQUIREMENT)) {
        return intl.formatMessage({
          id: "match.companyRanking.blankExpertiseRefused",
          defaultMessage: "An expertise requirement is still empty. Fill it in or remove it, then run again.",
        });
      }
      return userErrorMessage(intl, editor.solverError);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editor.solverError, editor.solverLoading],
  );

  // Closing means two different things and neither is a no-op: on a saved ranking it puts the list
  // back (the live draft, if any, is still behind it), and on a live one it discards the run. The
  // second asks first, because an unsaved ranking is gone once the draft is cleared — the same
  // guard the person result step has for the same loss.
  function close() {
    if (openedResult) {
      closeSavedResult();
      return;
    }
    if (editor.solverResult && !editor.solverResultSaved) {
      setConfirmDiscardOpen(true);
      return;
    }
    editor.clearSolverResult();
  }

  function discardRun() {
    setConfirmDiscardOpen(false);
    editor.clearSolverResult();
  }

  async function handleOpenResult(id: string) {
    // Der Sprung nach oben bleibt hier: der Hook beantwortet "welches Ergebnis", nicht "wohin
    // scrollt die Seite danach".
    if (await openSavedResult(id)) window.scrollTo({ top: 0, behavior: "smooth" });
  }

  async function handleSave({
    name,
    notes,
    hiddenSections,
  }: {
    name: string;
    notes?: string;
    hiddenSections?: string[];
  }) {
    const resultId = editor.solverResult?.id ?? null;
    await editor.saveResult({ name, notes, hiddenSections });
    setSaveOpen(false);
    if (resultId) await openSavedResult(resultId);
  }


  return (
    <div className="space-y-6">
      <ConfirmDialog
        open={confirmDiscardOpen}
        title={
          <FormattedMessage
            id="match.result.discardUnsavedConfirm.title"
            defaultMessage="Ungespeichertes Ergebnis verwerfen?"
          />
        }
        message={
          <FormattedMessage
            id="match.result.discardUnsavedConfirm.message"
            defaultMessage="Das berechnete Ergebnis wurde noch nicht gespeichert und geht beim Zurückgehen verloren."
          />
        }
        confirmLabel={
          <FormattedMessage id="match.result.discardUnsavedConfirm.confirm" defaultMessage="Verwerfen" />
        }
        onConfirm={discardRun}
        onCancel={() => setConfirmDiscardOpen(false)}
      />
      {exportOpen && exportResult ? (
        <MatchResultPdfExportModal
          open
          onClose={() => setExportOpen(false)}
          data={data}
          result={exportResult}
        />
      ) : null}
      {isSaved ? (
        <div className="smycles-field-label">
          <FormattedMessage
            id="match.result.savedResultLabel"
            defaultMessage="Gespeichertes Ergebnis · {name}"
            values={{
              name:
                openedResult?.name ??
                intl.formatMessage({ id: "match.result.unnamed", defaultMessage: "ohne Namen" }),
            }}
          />
        </div>
      ) : null}
      {isSaved || hasRanking ? (
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <Button variant="secondary" size="sm" onClick={close}>
            <FormattedMessage id="match.result.backToSelection" defaultMessage="← Schließen" />
          </Button>
          {exportResult ? (
            <Button variant="secondary" size="sm" onClick={() => setExportOpen(true)}>
              <FormattedMessage id="match.pdf.openExport" defaultMessage="PDF export" />
            </Button>
          ) : null}
          {isSaved && editor.canEditResults ? (
            <div className="ml-auto flex">
              <Button variant="secondary" size="sm" onClick={() => setSectionsOpen((open) => !open)} aria-expanded={sectionsOpen}>
                <FormattedMessage id="match.result.sectionPicker.trigger" defaultMessage="Abschnitte" />
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}
      {usesOrganizationalRecord ? (
        <div className="mb-2">
          <OrganizationalRecordBadge id="company-ranking-organizational-record-badge" />
        </div>
      ) : null}

      {isSaved && editor.canEditResults && (sectionsOpen || sectionsError) ? (
        <div className="space-y-3">
          {sectionsOpen ? (
            <ResultSectionPicker
              sectionIds={COMPANY_RESULT_SECTION_IDS}
              labels={companyResultSectionLabels}
              hidden={hiddenSections}
              onChange={handleSectionsChange}
              disabled={sectionsSaving}
            />
          ) : null}
          {sectionsError ? <div role="alert" className="box bg-rose-50 p-4 text-smycles-meta text-rose-700">{sectionsError}</div> : null}
        </div>
      ) : null}

      {runError ? (
        // NOT the person path's SolverErrorBanner, which is titled "Solver error": a company run
        // never calls the solver, so that heading would name a component that was not involved.
        // Neutral box, localized message, and the repair beside it.
        <div role="status" className="box flex items-center gap-4 bg-surface p-4 text-smycles-meta text-ink">
          <span className="min-w-0 flex-1">{runError}</span>
          <Button variant="secondary" size="sm" onClick={() => void editor.runSolver()}>
            <FormattedMessage id="common.tryAgain" defaultMessage="Erneut versuchen" />
          </Button>
        </div>
      ) : null}
      {openError ? (
        // Already localized at the throw site; kept neutral because a result that will not load is
        // a state, not a fault of the reader.
        <div role="status" className="box bg-surface p-4 text-smycles-meta text-ink">
          {openError}
        </div>
      ) : null}

      {rankingReadError ? (
        // THE RANKING READ SPEAKS FOR ITSELF. Without this the failure fell through to the panel's
        // empty state and was told as "the pool of this match is empty" — a sentence that cannot be
        // true for a match that produced a result (the run is gated on a non-empty pool), with no
        // message, no support code and no retry, because createErrorLink only logs. Neutral box and
        // our own copy: the subject line says what failed, the meta line carries genesis's support
        // code, and the button is the way out (@adr SPARK-0010-no-raw-server-errors-in-ui@1 rejects silent failure explicitly).
        <div role="status" className="box flex items-center gap-4 bg-surface p-4 text-smycles-meta text-ink">
          <span className="min-w-0 flex-1">
            <span className="block">
              {intl.formatMessage({
                id: "match.companyRanking.readFailed",
                defaultMessage: "Die Rangliste konnte nicht geladen werden.",
              })}
            </span>
            <span className="smycles-field-meta mt-1 block text-ink-muted">{rankingReadError}</span>
          </span>
          <Button variant="secondary" size="sm" onClick={() => void rankingQuery.refetch()}>
            <FormattedMessage id="common.tryAgain" defaultMessage="Erneut versuchen" />
          </Button>
        </div>
      ) : null}

      {/* NO REFUSED-WRITE CARD ANY MORE. It stated a refused pool WRITE, and the only pool write this
          screen ever made was the shortlist tick — retired by concept v18 (a). This step now reads
          the pool and reloads it, nothing else, so `pool.refusal` cannot become non-null here and
          the card was unreachable. The read-failure card below is a different event and stays. */}
      {pool.readError ? (
        // A failed pool READ, which is the reason the count below is unknown. Unsaid, it became the
        // most confident wrong sentence on the screen: "0 Unternehmen", "go and fill the pool", and
        // a dead run button, permanently, for a pool this screen merely could not load.
        <div role="status" className="box flex items-center gap-4 bg-surface p-4 text-smycles-meta text-ink">
          <span className="min-w-0 flex-1">
            <span className="block">
              {intl.formatMessage({
                id: "match.companyPool.readFailed",
                defaultMessage: "Der Unternehmens-Pool konnte nicht geladen werden.",
              })}
            </span>
            <span className="smycles-field-meta mt-1 block text-ink-muted">{pool.readError}</span>
          </span>
          <Button variant="secondary" size="sm" onClick={() => void pool.reload()}>
            <FormattedMessage id="common.tryAgain" defaultMessage="Erneut versuchen" />
          </Button>
        </div>
      ) : null}

      {shownResultId == null ? (
        <PreRunState
          editor={editor}
          poolTotal={poolTotal}
          poolLoading={pool.loading}
          criteriaCount={criteriaCount}
          incompleteCriteria={incompleteCriteria}
        />
      ) : rankingQuery.loading && ranked.length === 0 ? (
        // Said, rather than left to the panel's empty state: "there is nothing to rank" and "the
        // ranking has not arrived yet" are different sentences, and the second must not be told as
        // the first while the read is in flight.
        <div className="box bg-surface p-5 text-smycles-meta text-ink-muted">
          {intl.formatMessage({ id: "common.loading", defaultMessage: "Laden…" })}
        </div>
      ) : rankingReadError !== null && ranked.length === 0 ? (
        // WHERE THE FALSE SENTENCE USED TO BE PRINTED — the same distinction the branch above
        // already draws, for the two absences it did not cover. The failure is stated at the top of
        // the screen, with a support code and a retry; the panel is deliberately not drawn, because
        // its empty state would answer a failed read with a claim about the pool. A read that
        // failed OVER a cached ranking keeps showing that ranking: the card says the refresh
        // failed, and hiding rows the reader already has would be a second wrong answer.
        null
      ) : (
        <CompanyRankingPanel
          ranked={ranked}
          logoOf={logoOf}
          hiddenSections={isSaved ? hiddenSections : undefined}
          showHistoricalValueNote={isSaved && showsTodaysValues(ranked)}
        />
      )}

      {solveProgress}
      {!isSaved ? <SavedRankings editor={editor} onOpen={handleOpenResult} /> : null}

      {editor.canEditResults && hasRanking && !isSaved && !editor.solverResultSaved ? (
        // Same floating placement as the person result step: sticky (not fixed) so it respects the
        // main column's right edge, and the wrapper passes clicks through.
        <div className="pointer-events-none sticky bottom-4 z-10 flex justify-end sm:bottom-8">
          <Button
            variant="primary"
            onClick={() => setSaveOpen(true)}
            className="pointer-events-auto px-5 py-2.5 text-smycles-label shadow-xl"
          >
            <FormattedMessage id="match.result.saveResult" defaultMessage="Ergebnis speichern" />
          </Button>
        </div>
      ) : null}

      {saveOpen ? (
        <SaveMatchModal
          defaultName={data.matchTitle || data.projectName}
          defaultNotes=""
          onClose={() => setSaveOpen(false)}
          onSave={handleSave}
          sectionChoice={{
            sectionIds: COMPANY_RESULT_SECTION_IDS,
            labels: companyResultSectionLabels,
          }}
          // No preview persons, and no team size: a company ranking has neither. The pool figure is
          // the company count — for this mode `Match.candidateCount` counts companies — and the
          // requirement count is the COMPANY criteria, not `data.conditions`, which a company run
          // never evaluates.
          previewPersons={[]}
          previewMeta={{
            teamSize: null,
            candidateCount: editor.solverResult?.candidateCount ?? pool.total,
            requirementsCount: criteriaCount,
            agenticAi: Boolean(data.agenticAi),
            matchMode: resolveMatchMode(data.matchMode),
          }}
        />
      ) : null}
      {backSlot && <FloatingActionRow>{backSlot}</FloatingActionRow>}
    </div>
  );
}

/**
 * True when the evidence on screen falls back to the company as it is TODAY.
 *
 * genesis records the value it judged on every criterion atom, so a fresh ranking prints the state
 * at the time of the run and needs no caveat. A ranking stored before that carries no such value,
 * and its drill-down reads the company's current industry and head count instead — which is worth
 * saying out loud, and only then.
 */
function showsTodaysValues(ranked: readonly RankedCompany[]): boolean {
  return ranked.some((row) => row.criteria.some((criterion) => !criterion.actualRecorded));
}

// Before the first run: what will be judged, against what, and the one button that starts it.
// Deliberately not PreSolveState — that panel speaks of a solver, seats and a locked team, and its
// own mode table excludes the company arm for exactly that reason.
function PreRunState({
  editor,
  poolTotal,
  poolLoading,
  criteriaCount,
  incompleteCriteria,
}: {
  editor: UseMatchEditorResult;
  /**
   * The pool's size, or null when it is NOT KNOWN — the read is still in flight, or it failed. A
   * plain number cannot carry that: 0 is what both of those report and what an empty pool reports,
   * and every sentence below turns on which of the three it is.
   */
  poolTotal: number | null;
  /** Why the size is unknown, for the one line that has something different to say per reason. */
  poolLoading: boolean;
  criteriaCount: number;
  incompleteCriteria: number;
}) {
  const intl = useIntl();
  const running = editor.solverLoading;
  return (
    <div className="box bg-surface px-8 py-5 @lg:px-12 @lg:py-6">
      <div className="text-smycles-micro tracking-[0.25em] text-ink-muted">
        {/* The PERSON side's key, deliberately shared: this card is the pre-run state, and the
            person ranking labels that state "Bereit zum Ranking" (PreSolveState, RANK). Calling it
            "Rangliste" named the thing that does not exist yet. */}
        {intl.formatMessage({ id: "match.preSolve.eyebrowRank", defaultMessage: "Bereit zum Ranking" })}
      </div>
      <h3 className="mt-3 font-serif text-smycles-display-s leading-tight">
        {intl.formatMessage({
          id: "match.companyRanking.preRunHeading",
          defaultMessage:
            "Bewerte jedes Unternehmen im Pool einzeln gegen deine Anforderungen und erhalte eine Rangliste.",
        })}
      </h3>
      {/* THE PERSON CARD'S CHECKLIST, not a paragraph of counts (Kevin, 2026-09-09). The two facts
          the old meta line carried — how many companies, how many requirements — are the two rows'
          figures now, and each row also says whether that gate is actually PASSED, which the line
          never did. */}
      <ReadinessList
        items={[
          {
            label: intl.formatMessage({
              id: "match.companyRanking.poolLoadedRow",
              defaultMessage: "Unternehmen geladen",
            }),
            // Ticked only for a pool that is known AND non-empty. `poolTotal == null` is "not read
            // yet", and ticking that would promise a pool nobody has counted.
            ok: poolTotal != null && poolTotal > 0,
            hint:
              poolTotal != null
                ? intl.formatMessage(
                    // The person card's own hint key: "{n} im Pool" needs no company wording.
                    { id: "match.preSolve.poolOnly", defaultMessage: "{n} im Pool" },
                    { n: poolTotal },
                  )
                : poolLoading
                  ? intl.formatMessage({ id: "common.loading", defaultMessage: "Laden…" })
                  : undefined,
          },
          {
            label: intl.formatMessage({
              id: "match.companyRanking.criteriaCompleteRow",
              defaultMessage: "Anforderungen vollständig",
            }),
            // A run with NO requirements is allowed and warned about below, so it is not a failed
            // gate; a half-written one is, because genesis would either abort or judge a blank.
            ok: incompleteCriteria === 0,
            hint: intl.formatMessage(
              {
                id: "match.companyRanking.criteriaMeta",
                defaultMessage: "{count, plural, one {# Anforderung} other {# Anforderungen}}",
              },
              { count: criteriaCount },
            ),
          },
        ]}
      />
      {!editor.actions?.canGenerateResult ? (
        <p className="smycles-field-meta mt-3 max-w-xl text-ink-muted" role="status">
          {intl.formatMessage({
            id: "match.companyRanking.runNoRight",
            defaultMessage:
              "Du kannst für dieses Match derzeit keine Rangliste erzeugen.",
          })}
        </p>
      ) : poolTotal === 0 ? (
        // STRICTLY zero, and only zero once the size is known: null is "not loaded yet", and
        // answering that with "go and fill the pool" is exactly the wrong instruction — the pool
        // may be full, and the reader would be sent to a step that has nothing wrong with it.
        <p className="smycles-field-meta mt-3 max-w-xl text-ink-muted">
          {intl.formatMessage({
            id: "match.companyRanking.needPool",
            defaultMessage:
              "Nimm im Schritt Unternehmen mindestens ein Unternehmen in den Pool auf – ohne Pool gibt es nichts zu ranken.",
          })}
        </p>
      ) : criteriaCount === 0 ? (
        <p className="smycles-field-meta mt-3 max-w-xl text-ink-muted">
          {intl.formatMessage({
            id: "match.companyRanking.noCriteriaWarning",
            defaultMessage:
              "Ohne Unternehmens-Anforderungen entsteht eine Rangliste ohne Nachweis: jedes Unternehmen trägt dann denselben leeren Nachweis.",
          })}
        </p>
      ) : incompleteCriteria > 0 ? (
        // Neutral and textual, like every other state on this surface: the run is unavailable, and
        // the sentence says what to do about it instead of leaving the button dead.
        <p role="status" className="smycles-field-meta mt-3 max-w-xl text-ink-muted">
          {intl.formatMessage(
            {
              id: "match.companyRanking.criteriaIncomplete",
              defaultMessage:
                "{count, plural, one {# Anforderung ist unvollständig} other {# Anforderungen sind unvollständig}} – im Schritt Anforderungen ausfüllen oder entfernen, dann lässt sich ranken.",
            },
            { count: incompleteCriteria },
          )}
        </p>
      ) : null}
      <div className="mt-5 flex flex-wrap items-center gap-3">
        <Button
          variant="primary"
          size="lg"
          onClick={() => void editor.runSolver()}
          // A half-written criterion is a hard gate and not a warning: it would either abort in
          // genesis (an empty UUID) or be judged as written (a blank wording).
          // `poolTotal === 0` and never `!poolTotal`: an unknown size is null, and disabling on
          // that killed the button on every cold load of this step for a pool that was in fact
          // full. An empty pool is a reason to refuse the run; not having read the pool yet is not.
          disabled={running || poolTotal === 0 || !editor.actions?.canGenerateResult || incompleteCriteria > 0}
        >
          {running
            ? intl.formatMessage({
                id: "match.companyRanking.running",
                defaultMessage: "Ranke Unternehmen…",
              })
            : intl.formatMessage({
                id: "match.companyRanking.run",
                defaultMessage: "Unternehmen ranken",
              })}
        </Button>
      </div>
    </div>
  );
}

// The saved rankings of this match. Its own list rather than the person step's history for one
// reason that matters: that list counts "Kandidaten" and shows a team size, and for a company match
// both would be wrong words for the same numbers.
function SavedRankings({
  editor,
  onOpen,
}: {
  editor: UseMatchEditorResult;
  onOpen: (id: string) => Promise<void>;
}) {
  const intl = useIntl();
  const [pendingDelete, setPendingDelete] = useState<MatchResultListItem | null>(null);
  const [deleting, setDeleting] = useState(false);

  if (editor.resultsLoading && editor.results.length === 0) {
    return (
      <div className="text-smycles-label text-ink-muted">
        <FormattedMessage
          id="match.result.historyLoading"
          defaultMessage="Gespeicherte Ergebnisse laden …"
        />
      </div>
    );
  }
  if (editor.results.length === 0) return null;

  const unnamed = intl.formatMessage({ id: "match.result.unnamed", defaultMessage: "ohne Namen" });

  async function confirmDelete() {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await editor.deleteResult(pendingDelete.id);
      setPendingDelete(null);
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div className="box bg-surface p-5">
      <BoxHeader
        title={
          <FormattedMessage id="match.result.historyTitle" defaultMessage="Gespeicherte Ergebnisse" />
        }
        right={<span className="text-smycles-label tabular-nums text-ink-muted">{editor.results.length}</span>}
      />
      <ul className="divide-y divide-ink/5">
        {editor.results.map((result) => (
          <li key={result.id} className="flex items-center gap-3 py-2.5">
            <button
              onClick={() => void onOpen(result.id)}
              className="-mx-2 min-w-0 flex-1 cursor-pointer rounded-md px-2 py-1 text-left transition hover:bg-ink/[0.06]"
            >
              <div className="flex min-w-0 items-center gap-2">
                <div className="truncate text-smycles-meta font-medium">{result.name ?? unnamed}</div>
                {result.usesOrganizationalRecord ? (
                  <OrganizationalRecordBadge id={`saved-ranking-organizational-record-${result.id}`} insideInteractive />
                ) : null}
              </div>
              <div className="text-smycles-micro text-ink-muted">
                {[
                  result.createdAt ? formatDate(result.createdAt, intl.locale) : null,
                  // The stored candidateCount, read as what it is in this mode: the number of
                  // COMPANIES the run judged.
                  intl.formatMessage(
                    {
                      id: "match.companyRanking.companiesMeta",
                      defaultMessage: "{count, plural, one {# Unternehmen} other {# Unternehmen}}",
                    },
                    { count: result.candidateCount ?? 0 },
                  ),
                  result.createdBy?.formattedName ?? null,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </div>
              {result.notes ? (
                <div className="mt-1 truncate text-smycles-label italic text-ink-muted">{result.notes}</div>
              ) : null}
            </button>
            {/* Deleting a saved result is a RESULT write (`match` WRITE), not a pool write — see
                canEditResults. The two axes coincide for every role spark issues, and they are
                still different questions. */}
            {editor.canEditResults ? (
              <Button
                variant="icon"
                size="sm"
                onClick={() => setPendingDelete(result)}
                title={intl.formatMessage({ id: "match.result.deleteTitle", defaultMessage: "Löschen" })}
                aria-label={intl.formatMessage(
                  {
                    id: "match.result.deleteAriaLabel",
                    defaultMessage: "Ergebnis „{name}“ löschen",
                  },
                  { name: result.name ?? unnamed },
                )}
              >
                ✕
              </Button>
            ) : null}
          </li>
        ))}
      </ul>

      <ConfirmDialog
        open={pendingDelete !== null}
        title={intl.formatMessage({
          id: "match.result.deleteConfirmTitle",
          defaultMessage: "Ergebnis löschen?",
        })}
        message={
          pendingDelete ? (
            <p>
              <FormattedMessage
                id="match.result.deleteConfirmBody"
                defaultMessage="Das gespeicherte Ergebnis <b>„{name}“</b> wird endgültig entfernt. Diese Aktion kann nicht rückgängig gemacht werden."
                values={{
                  name: pendingDelete.name ?? unnamed,
                  b: (chunks) => <span className="font-medium text-ink">{chunks}</span>,
                }}
              />
            </p>
          ) : null
        }
        confirmLabel={
          deleting
            ? intl.formatMessage({ id: "match.result.deleting", defaultMessage: "Lösche…" })
            : intl.formatMessage({
                id: "match.result.deleteFinal",
                defaultMessage: "Endgültig löschen",
              })
        }
        cancelLabel={intl.formatMessage({ id: "common.cancel", defaultMessage: "Abbrechen" })}
        destructive
        onConfirm={confirmDelete}
        onCancel={() => {
          if (!deleting) setPendingDelete(null);
        }}
      />
    </div>
  );
}
